import { useEffect, useMemo, useRef, useState } from 'react'
import Notiflix from 'notiflix'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { useAuthUserData } from '../../../authUserProvider'
import { InputFieldText } from '../../reusable/inputFieldText'
import { InputFieldAddressLookup } from '../../reusable/inputFieldAddressLookup'
import styles from './styles.module.scss'

interface FormState {
    title: string
    firstname: string
    lastname: string
    email: string
    telephone: string
    dob: string
    house_number: string
    house_name: string
    flat: string
    street: string
    town_city: string
    county: string
    postcode: string
}

interface CreatedClient {
    id: number
    firstname?: string | null
    lastname?: string | null
    email?: string | null
    telephone?: string | null
    dob?: string | null
}

interface SendResult {
    sendType: 'email' | 'sms'
    success: boolean
    esignUrl?: string
    message?: string
    sentTo: string
    reused?: boolean
}

const EMPTY_FORM: FormState = {
    title: '',
    firstname: '',
    lastname: '',
    email: '',
    telephone: '',
    dob: '',
    house_number: '',
    house_name: '',
    flat: '',
    street: '',
    town_city: '',
    county: '',
    postcode: '',
}

const TITLES = ['Mr', 'Mrs', 'Miss', 'Ms', 'Mx', 'Dr', 'Prof', 'Rev']

// Latest date a client could be born to be at least 21 today (YYYY-MM-DD).
const maxDobForAge21 = () => {
    const d = new Date()
    d.setFullYear(d.getFullYear() - 21)
    return d.toISOString().slice(0, 10)
}

const isValidDob = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v.trim())

const isAtLeast21 = (isoDob: string) => {
    if (!isValidDob(isoDob)) return false
    const dob = new Date(`${isoDob}T00:00:00`)
    if (Number.isNaN(dob.getTime())) return false
    const today = new Date()
    let age = today.getFullYear() - dob.getFullYear()
    const monthDiff = today.getMonth() - dob.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--
    return age >= 21
}

// Parses the comma-joined string emitted by InputFieldAddressLookup into our
// schema fields. Data8's Retrieve response yields address lines like:
//   "1 High Street,Camberley,Surrey,GU15 3SX"
//   "Harley House,29 Cambray Place,CHELTENHAM,Gloucestershire,GL50 1JN"
//   "Apartment 102,Ambassador Apartments,Waterman Walk,SALFORD,M50 3AU"
//   "Flat 2,29 High Street,LONDON,Greater London,SW1 2AB"
//
// Strategy:
//   1. Anchor the post-town by finding the LAST line in ALL CAPS before the
//      postcode (Royal Mail PAF convention — post-towns are always uppercase,
//      counties / dependent localities are title-case). This works regardless
//      of whether a county is present.
//   2. Anything between the post-town and the postcode becomes county.
//   3. From the remaining "street lines", peel off any Flat/Apartment prefix.
//   4. Find the first line that begins with a number — that's the street, and
//      its leading digit is the house number. Anything BEFORE the numbered
//      line becomes the house name.
//   5. If no numbered line exists, treat the first line as a house name and
//      the remainder as the street.
const parseAddressLines = (joined: string): Partial<FormState> => {
    const parts = joined
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)
    if (parts.length === 0) return {}

    const postcode = parts[parts.length - 1] || ''
    let county = ''
    let town = ''
    let streetLines: string[] = []

    const isAllCaps = (s: string) => Boolean(s) && s === s.toUpperCase() && /[A-Z]/.test(s)

    // 1. Walk backwards from second-to-last looking for an ALL CAPS line — that's the post-town.
    let townIdx = -1
    for (let i = parts.length - 2; i >= 0; i--) {
        if (isAllCaps(parts[i])) {
            townIdx = i
            break
        }
    }

    if (townIdx >= 0) {
        town = parts[townIdx]
        // 2. Lines between town and postcode (exclusive) are county / dependent locality.
        if (townIdx < parts.length - 2) {
            county = parts.slice(townIdx + 1, parts.length - 1).join(', ')
        }
        streetLines = parts.slice(0, townIdx)
    } else {
        // Fallback for partial / non-Royal-Mail data: positional layout.
        if (parts.length === 1 || parts.length === 2) {
            streetLines = [parts[0]]
        } else if (parts.length === 3) {
            streetLines = [parts[0]]
            town = parts[1]
        } else if (parts.length === 4) {
            streetLines = [parts[0]]
            town = parts[1]
            county = parts[2]
        } else {
            streetLines = parts.slice(0, parts.length - 3)
            town = parts[parts.length - 3]
            county = parts[parts.length - 2]
        }
    }

    let flat = ''
    let house_number = ''
    let house_name = ''
    let street = ''

    // 1. Pull out a Flat/Apartment/Apt/Suite/Unit prefix line if present.
    const flatRegex = /^(Flat|Apartment|Apt|Suite|Unit)\s+\S+$/i
    for (let i = 0; i < streetLines.length; i++) {
        if (flatRegex.test(streetLines[i])) {
            flat = streetLines[i]
            streetLines.splice(i, 1)
            break
        }
    }

    // 2. Find the first line starting with a number — that's the street with house no.
    const numericRegex = /^(\d+[a-zA-Z]?)\s+(.+)$/
    let numericIdx = -1
    for (let i = 0; i < streetLines.length; i++) {
        if (numericRegex.test(streetLines[i])) {
            numericIdx = i
            break
        }
    }

    if (numericIdx >= 0) {
        const match = streetLines[numericIdx].match(numericRegex) as RegExpMatchArray
        house_number = match[1]
        const streetName = match[2]
        // Lines BEFORE the numbered line are the house name(s).
        if (numericIdx > 0) {
            house_name = streetLines.slice(0, numericIdx).join(', ')
        }
        // Lines AFTER (rare) get appended onto the street.
        const tail = streetLines.slice(numericIdx + 1)
        street = tail.length ? [streetName, ...tail].join(', ') : streetName
    } else if (streetLines.length > 1) {
        // No numbered line but multiple lines — first is house name, rest is street.
        house_name = streetLines[0]
        street = streetLines.slice(1).join(', ')
    } else {
        street = streetLines.join(', ')
    }

    return {
        flat,
        house_number,
        house_name,
        street,
        town_city: town,
        county,
        postcode,
    }
}

export const PcpReferralAddClientView = () => {
    const authUser = useAuthUserData()
    const agentLabel = authUser?.name || authUser?.email || 'unknown'

    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [isSaving, setIsSaving] = useState(false)
    const [isSending, setIsSending] = useState<null | 'email' | 'sms'>(null)
    const [createdClient, setCreatedClient] = useState<CreatedClient | null>(null)
    // Unified inline error for both client-side validation failures and backend
    // rejections. Rendered immediately under the Save button so the user can
    // see exactly why their submit didn't go through.
    const [saveError, setSaveError] = useState<string | null>(null)
    const [lastSendResult, setLastSendResult] = useState<SendResult | null>(null)
    const [selectedAddressRaw, setSelectedAddressRaw] = useState<string>('')
    // Bumping this remounts InputFieldText / InputFieldAddressLookup so their internal
    // state clears (they're effectively uncontrolled once mounted).
    const [formKey, setFormKey] = useState(0)
    const successRef = useRef<HTMLDivElement | null>(null)

    const maxDob = useMemo(maxDobForAge21, [])

    // After a successful create, the form collapses and we scroll the success
    // panel into view so the next-step buttons are obviously the only thing
    // remaining on screen.
    useEffect(() => {
        if (createdClient && successRef.current) {
            successRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }, [createdClient])

    const setField = <K extends keyof FormState>(k: K, v: FormState[K]) => {
        setForm((f) => ({ ...f, [k]: v }))
        if (saveError) setSaveError(null)
    }

    const onChange = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setField(k, e.target.value)
    }

    // Single dispatcher used by the InputFieldText (email/telephone) and
    // InputFieldAddressLookup reusable components.
    //
    // InputFieldText sets its own inline error via Data8 then unconditionally
    // calls saveValue — and if we return { success: true } it WIPES that error
    // immediately (`saveResult.success ? setError("") : setError(saveResult.error)`).
    // To make the validation feedback stick, when InputFieldText has stripped
    // the value to '' (its "validation failed" signal), we return success:false
    // with the appropriate message so the inline red error persists.
    const handleReusableSave = ({ field, value }: { field: string; value: string }) => {
        if (field === 'email') {
            setField('email', value)
            if (value === '') return { success: false, error: 'Invalid email' }
        } else if (field === 'telephone') {
            setField('telephone', value)
            if (value === '') return { success: false, error: 'Invalid telephone number' }
        } else if (field === 'address') {
            setSelectedAddressRaw(value)
            const parsed = parseAddressLines(value)
            setForm((f) => ({
                ...f,
                flat: parsed.flat ?? f.flat,
                house_number: parsed.house_number ?? f.house_number,
                house_name: parsed.house_name ?? f.house_name,
                street: parsed.street ?? f.street,
                town_city: parsed.town_city ?? f.town_city,
                county: parsed.county ?? f.county,
                postcode: parsed.postcode ?? f.postcode,
            }))
            if (saveError) setSaveError(null)
        }
        return { success: true, error: '' }
    }

    const validate = (): string | null => {
        const required: [keyof FormState, string][] = [
            ['firstname', 'First name'],
            ['lastname', 'Last name'],
            ['email', 'Email'],
            ['telephone', 'Telephone'],
            ['dob', 'Date of birth'],
        ]
        for (const [k, label] of required) {
            if (!form[k] || !form[k].trim()) {
                return `${label} is required (check for validation errors above)`
            }
        }
        if (!isValidDob(form.dob)) return 'Date of birth must be a valid date'
        if (!isAtLeast21(form.dob)) return 'Client must be at least 21 years old'
        return null
    }

    const resetForm = () => {
        setForm(EMPTY_FORM)
        setCreatedClient(null)
        setLastSendResult(null)
        setSaveError(null)
        setSelectedAddressRaw('')
        setFormKey((k) => k + 1)
    }

    const handleReset = () => {
        Confirm.show(
            'Start a new client?',
            'This will clear the form and the last result. Continue?',
            'Yes, reset',
            'Cancel',
            resetForm,
        )
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const err = validate()
        if (err) {
            setSaveError(err)
            return
        }
        setSaveError(null)
        setIsSaving(true)
        try {
            const payload = {
                ...form,
                title: form.title || null,
                added_by: agentLabel,
            }
            const res = await api({ url: '/pcp-referral-create-client', data: payload })
            if (res.data?.success && res.data.client) {
                setCreatedClient(res.data.client as CreatedClient)
                setLastSendResult(null)
                Notiflix.Notify.success('Client created — you can now send an e-sign link below')
            } else {
                setSaveError(res.data?.message || 'Could not create client (unknown error)')
            }
        } catch (error: any) {
            const message = error?.response?.data?.message || error?.message || 'Unknown error'
            setSaveError(`Could not create client: ${message}`)
        } finally {
            setIsSaving(false)
        }
    }

    const handleSend = (sendType: 'email' | 'sms') => {
        if (!createdClient) return
        const recipient = sendType === 'email' ? createdClient.email : createdClient.telephone
        if (!recipient) {
            Report.failure(
                'Missing contact detail',
                `Client has no ${sendType === 'email' ? 'email' : 'telephone'} on record.`,
                'Okay',
            )
            return
        }
        Confirm.show(
            `Send e-sign link via ${sendType.toUpperCase()}?`,
            `This will send the CFA e-sign link to ${recipient}. Continue?`,
            'Send',
            'Cancel',
            async () => {
                setIsSending(sendType)
                try {
                    const res = await api({
                        url: '/pcp-referral-send-esign-link',
                        data: {
                            client_id: createdClient.id,
                            sendType,
                            agent: agentLabel,
                        },
                    })
                    if (res.data?.success) {
                        setLastSendResult({
                            sendType,
                            success: true,
                            esignUrl: res.data.esignUrl,
                            message: res.data.message,
                            sentTo: recipient,
                            reused: res.data.reused,
                        })
                        Notiflix.Notify.success(`E-sign link sent via ${sendType.toUpperCase()}`)
                    } else {
                        const message = res.data?.message || 'Failed to send link'
                        setLastSendResult({
                            sendType,
                            success: false,
                            message,
                            sentTo: recipient,
                        })
                        Report.failure('Send failed', message, 'Okay')
                    }
                } catch (error: any) {
                    const message = error?.response?.data?.message || error?.message || 'Unknown error'
                    setLastSendResult({ sendType, success: false, message, sentTo: recipient })
                    Report.failure('Send failed', message, 'Okay')
                } finally {
                    setIsSending(null)
                }
            },
        )
    }

    return (
        <div className={styles.container}>

            {createdClient && (
                <div className={styles.successBox} ref={successRef}>
                    <h3>
                        Client created — {createdClient.firstname} {createdClient.lastname} (#{createdClient.id})
                    </h3>
                    <p>
                        Saved to the PCP Referral schema with the contact details below. Send the CFA e-sign link now,
                        or start a new client.
                    </p>
                    <div className={styles.actionRow}>
                        <button
                            type="button"
                            className={styles.sendButton}
                            disabled={isSending !== null}
                            onClick={() => handleSend('email')}
                        >
                            {isSending === 'email' ? 'Sending…' : 'Send Esign Link by Email'}
                        </button>
                        <button
                            type="button"
                            className={styles.sendButton}
                            disabled={isSending !== null || !createdClient.telephone}
                            onClick={() => handleSend('sms')}
                        >
                            {isSending === 'sms' ? 'Sending…' : 'Send Esign Link by SMS'}
                        </button>
                        <button type="button" className={styles.subtleButton} onClick={resetForm}>
                            + Add Another Client
                        </button>
                    </div>

                    {lastSendResult && (
                        <div className={`${styles.sendResult} ${lastSendResult.success ? '' : styles.sendResultError}`}>
                            <h4>
                                {lastSendResult.success
                                    ? `Sent via ${lastSendResult.sendType.toUpperCase()} to ${lastSendResult.sentTo}`
                                    : `Failed to send via ${lastSendResult.sendType.toUpperCase()}`}
                            </h4>
                            {lastSendResult.message && <p style={{ margin: '0 0 0.5rem' }}>{lastSendResult.message}</p>}
                            {lastSendResult.esignUrl && (
                                <p style={{ margin: 0 }}>
                                    Link:{' '}
                                    <a
                                        className={styles.link}
                                        href={lastSendResult.esignUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {lastSendResult.esignUrl}
                                    </a>
                                    {lastSendResult.reused ? ' (reused existing token)' : ''}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {!createdClient && (
            <form className={styles.formMainContainer} key={formKey} onSubmit={handleSubmit}>
                <div className={styles.formSection}>
                    <h3>Personal Details</h3>
                    <div className={styles.grid}>
                        <div className={styles.field}>
                            <label>Title</label>
                            <div className={styles.inputCol}>
                                <select value={form.title} onChange={onChange('title')}>
                                    <option value="">—</option>
                                    {TITLES.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label>
                                First Name<span className={styles.req}>*</span>
                            </label>
                            <div className={styles.inputCol}>
                                <input value={form.firstname} onChange={onChange('firstname')} autoComplete="off" />
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label>
                                Last Name<span className={styles.req}>*</span>
                            </label>
                            <div className={styles.inputCol}>
                                <input value={form.lastname} onChange={onChange('lastname')} autoComplete="off" />
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label>
                                Date of Birth<span className={styles.req}>*</span>
                            </label>
                            <div className={styles.inputCol}>
                                <input type="date" value={form.dob} onChange={onChange('dob')} max={maxDob} />
                                <small>Client must be 21 or older.</small>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.formSection}>
                    <h3>Contact Details</h3>
                    <div className={styles.grid}>
                        <div className={styles.field}>
                            <label>
                                Email<span className={styles.req}>*</span>
                            </label>
                            <div className={styles.inputCol}>
                                <InputFieldText fieldName="email" fieldType="email" saveValue={handleReusableSave} />
                                <small>Validated by Data8 in real time.</small>
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label>
                                Telephone<span className={styles.req}>*</span>
                            </label>
                            <div className={styles.inputCol}>
                                <InputFieldText fieldName="telephone" fieldType="tel" saveValue={handleReusableSave} />
                                <small>UK mobile/landline — validated by Data8.</small>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.formSection}>
                    <h3>Address</h3>
                    <div className={styles.field} style={{ marginBottom: '1rem' }}>
                        <label>Search address (Data8)</label>
                        <div className={styles.inputCol}>
                            <InputFieldAddressLookup
                                fieldName="address"
                                saveValue={handleReusableSave}
                                enhanced={true}
                            />
                            <small>
                                Start typing the postcode or first line and pick from the list. Fields below will be
                                populated automatically — edit any of them if needed.
                            </small>
                        </div>
                    </div>
                    {selectedAddressRaw && (
                        <>
                            <div className={styles.selectedAddressNote}>
                                <strong>Selected:</strong>{' '}
                                {selectedAddressRaw
                                    .split(',')
                                    .map((p) => p.trim())
                                    .join(', ')}
                            </div>
                            <div className={styles.grid}>
                                <div className={styles.field}>
                                    <label>House Number</label>
                                    <div className={styles.inputCol}>
                                        <input
                                            value={form.house_number}
                                            onChange={onChange('house_number')}
                                        />
                                    </div>
                                </div>
                                <div className={styles.field}>
                                    <label>House Name</label>
                                    <div className={styles.inputCol}>
                                        <input
                                            value={form.house_name}
                                            onChange={onChange('house_name')}
                                        />
                                    </div>
                                </div>
                                <div className={styles.field}>
                                    <label>Flat / Apt</label>
                                    <div className={styles.inputCol}>
                                        <input value={form.flat} onChange={onChange('flat')} />
                                    </div>
                                </div>
                                <div className={styles.field}>
                                    <label>Street</label>
                                    <div className={styles.inputCol}>
                                        <input value={form.street} onChange={onChange('street')} />
                                    </div>
                                </div>
                                <div className={styles.field}>
                                    <label>Town / City</label>
                                    <div className={styles.inputCol}>
                                        <input
                                            value={form.town_city}
                                            onChange={onChange('town_city')}
                                        />
                                    </div>
                                </div>
                                <div className={styles.field}>
                                    <label>County</label>
                                    <div className={styles.inputCol}>
                                        <input value={form.county} onChange={onChange('county')} />
                                    </div>
                                </div>
                                <div className={styles.field}>
                                    <label>Postcode</label>
                                    <div className={styles.inputCol}>
                                        <input value={form.postcode} onChange={onChange('postcode')} />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className={styles.actionRow}>
                    <button type="submit" className={styles.primaryButton} disabled={isSaving}>
                        {isSaving ? 'Saving…' : 'Save Client'}
                    </button>
                    {(form.firstname || form.lastname || form.email) && !isSaving && (
                        <button type="button" className={styles.subtleButton} onClick={handleReset}>
                            Clear Form
                        </button>
                    )}
                </div>

                {saveError && (
                    <div className={styles.errorBox} role="alert" aria-live="polite">
                        <strong>Couldn&apos;t save:</strong> {saveError}
                    </div>
                )}

                <p className={styles.note}>
                    Logged in as <strong>{agentLabel}</strong> — this will be recorded against the new client&apos;s
                    e-sign link for agent stats.
                </p>
            </form>
            )}
        </div>
    )
}

export default PcpReferralAddClientView
